import type {
  ArtifactDraft,
  ArtifactType,
  Attachment,
  GenerateArtifactBatchRequest,
  GenerateArtifactRequest,
} from "@base-hormonal/contracts";
import type { TypedSupabaseClient } from "../../../shared/api/supabase";
import { fromSupabaseError, PublicAppError } from "../../../shared/lib/errors";
import { withRequestSignal } from "../../../shared/lib/request";
import { generateDraft, generateDraftBatch, reviseDraft } from "../../assistant/api/worker-client";

export interface GenerateDraftInput {
  accessToken: string;
  conversationId: string;
  artifactType: ArtifactType;
  sourceText: string;
  attachment?: Attachment | null;
  assetId?: string | null;
  payload?: GenerateArtifactRequest["payload"];
  signal?: AbortSignal;
}

export interface ExamDraftInput extends GenerateDraftInput {
  userId: string;
  profileId: string;
  file: File;
}

export interface GenerateDraftBatchInput {
  accessToken: string;
  conversationId: string;
  sourceText: string;
  expectedCount: number;
  payloads?: GenerateArtifactBatchRequest["payloads"];
  signal?: AbortSignal;
}

function raise(error: { code?: string; message?: string } | null): void {
  const safe = fromSupabaseError(error);
  if (safe !== null) throw safe;
}

function safeObjectName(name: string): string {
  return name.replace(/[^\p{L}\p{N}._-]+/gu, "-").slice(-120);
}

/**
 * A IA nunca materializa registro: gera rascunho, o humano edita e só o RPC
 * transacional `aprovar_ai_draft` cria o registro — inativo/rascunho — ou
 * `rejeitar_ai_draft` o encerra preservando a auditoria.
 */
export function createArtifactsApi(client: TypedSupabaseClient) {
  return {
    generate(input: GenerateDraftInput): Promise<ArtifactDraft> {
      return generateDraft(
        input.accessToken,
        {
          conversationId: input.conversationId,
          artifactType: input.artifactType,
          sourceText: input.sourceText,
          ...(input.attachment ? { attachment: input.attachment } : {}),
          ...(input.assetId === undefined ? {} : { assetId: input.assetId }),
          ...(input.payload === undefined ? {} : { payload: input.payload }),
        },
        input.signal,
      );
    },

    generateBatch(input: GenerateDraftBatchInput): Promise<ArtifactDraft[]> {
      return generateDraftBatch(
        input.accessToken,
        {
          conversationId: input.conversationId,
          artifactType: "avaliacao_corporal",
          sourceText: input.sourceText,
          expectedCount: input.expectedCount,
          ...(input.payloads === undefined ? {} : { payloads: input.payloads }),
        },
        input.signal,
      );
    },

    revise(
      accessToken: string,
      draftId: string,
      payload: unknown,
      signal?: AbortSignal,
    ): Promise<ArtifactDraft> {
      return reviseDraft(accessToken, draftId, payload, signal);
    },

    /**
     * Exame precisa do arquivo privado antes do rascunho. Se a geração falhar,
     * o objeto órfão é removido; se a remoção também falhar, o erro público
     * continua sanitizado e a interface avisa sem expor o path.
     */
    async generateExam(input: ExamDraftInput): Promise<ArtifactDraft> {
      const path = `${input.userId}/${input.profileId}/${crypto.randomUUID()}-${safeObjectName(input.file.name)}`;
      const upload = await client.storage
        .from("exames")
        .upload(path, input.file, { contentType: input.file.type, upsert: false });
      if (upload.error !== null) {
        throw new PublicAppError(
          "storage_failed",
          "Não foi possível guardar o arquivo privado do exame.",
        );
      }
      try {
        return await this.generate({ ...input, assetId: path });
      } catch (error: unknown) {
        await client.storage
          .from("exames")
          .remove([path])
          .catch(() => undefined);
        throw error;
      }
    },

    async approve(draftId: string): Promise<{ status: string; record_id?: string }> {
      return withRequestSignal(undefined, async (signal) => {
        const { data, error } = await client
          .rpc("aprovar_ai_draft", { p_draft_id: draftId })
          .abortSignal(signal);
        raise(error);
        const result = (data ?? {}) as { status?: string; record_id?: string };
        if (result.status === "expired") {
          throw new PublicAppError("draft_expired", "Este rascunho expirou; gere outro.");
        }
        return {
          status: result.status ?? "approved",
          ...(result.record_id ? { record_id: result.record_id } : {}),
        };
      });
    },

    async reject(draftId: string): Promise<void> {
      await withRequestSignal(undefined, async (signal) => {
        const { error } = await client
          .rpc("rejeitar_ai_draft", { p_draft_id: draftId })
          .abortSignal(signal);
        raise(error);
      });
    },

    async expire(): Promise<number> {
      return withRequestSignal(undefined, async (signal) => {
        const { data, error } = await client.rpc("expirar_ai_drafts").abortSignal(signal);
        raise(error);
        return typeof data === "number" ? data : 0;
      });
    },

    async purge(retentionDays = 60): Promise<number> {
      return withRequestSignal(undefined, async (signal) => {
        const { data, error } = await client
          .rpc("purgar_ai_drafts_antigos", { p_retencao_dias: retentionDays })
          .abortSignal(signal);
        raise(error);
        return typeof data === "number" ? data : 0;
      });
    },
  };
}

export type ArtifactsApi = ReturnType<typeof createArtifactsApi>;
